import { GreaterAreaDropdown } from '@/components/dropdowns';
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, PromptSelector, toast, useApiClient } from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { GreaterArea } from '@/config/objects/greater-areas';

interface GenerateAttractionFormData {
  name: string;
  greaterAreaId: string;
  tourId?: string;
  promptId?: string;
}

interface GenerateAttractionActionProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GenerateAttractionAction({ open, onOpenChange }: GenerateAttractionActionProps) {
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<GenerateAttractionFormData>({
    name: '',
    greaterAreaId: '',
  });

  // Fetch greater area details when selected
  const { data: greaterAreaResponse } = useQuery({
    queryKey: ['GreaterArea', 'detail', formData.greaterAreaId],
    queryFn: async () => {
      if (!formData.greaterAreaId) return null;
      return apiClient.get<{ success: boolean; data: GreaterArea }>(
        `/greater-areas/${formData.greaterAreaId}`,
      );
    },
    enabled: Boolean(formData.greaterAreaId),
  });

  const selectedGreaterArea = greaterAreaResponse?.data;

  const generateMutation = useMutation({
    mutationFn: async (values: GenerateAttractionFormData) => {
      return apiClient.post<{ success: boolean; data?: { id: string } }>('/attractions/generate', values);
    },
    onSuccess: (data: { success: boolean; data?: { id: string } }) => {
      queryClient.invalidateQueries({ queryKey: ['Attraction', 'list'] });
      onOpenChange(false);
      setFormData({ name: '', greaterAreaId: '' }); // Reset form
      toast.success('Attraction generated successfully');
      if (data?.data?.id) {
        navigate(`/attractions/${data.data.id}`);
      }
    },
    onError: (error: Error) => {
      toast.error(error?.message || 'Failed to generate attraction');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.greaterAreaId) {
      toast.error('Please fill in all required fields');
      return;
    }

    generateMutation.mutate(formData);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        onOpenChange(isOpen);
        if (!isOpen) {
          // Reset form when closing
          setFormData({ name: '', greaterAreaId: '' });
        }
      }}
    >
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Generate Attraction</DialogTitle>
            <DialogDescription>
              Generates a new attraction with content, location data, and more.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium mb-1">
                Name <span className="text-destructive">*</span>
              </label>
              <input
                id="name"
                type="text"
                required
                placeholder="Enter the name of the attraction"
                className="w-full px-3 py-2 border rounded-md"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            <div>
              <label htmlFor="greaterAreaId" className="block text-sm font-medium mb-1">
                Greater Area <span className="text-destructive">*</span>
              </label>
              <GreaterAreaDropdown
                value={formData.greaterAreaId}
                onChange={(value) => setFormData({ ...formData, greaterAreaId: value })}
                placeholder="Select greater area"
              />
            </div>

            <PromptSelector
              purpose="attraction_short_description"
              value={formData.promptId}
              onChange={(value) => setFormData({ ...formData, promptId: value })}
              label="Description Prompt"
            />

            {/* Show city and state from selected greater area */}
            {selectedGreaterArea && (
              <div className="p-3 bg-muted rounded-md">
                <p className="text-sm text-muted-foreground mb-1">
                  Location from selected greater area:
                </p>
                <p className="text-sm font-medium">
                  {selectedGreaterArea.city}, {selectedGreaterArea.state}
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={generateMutation.isPending}
            >
              Generate Attraction
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Export the icon for use in the header action
export const GenerateAttractionIcon = Sparkles;
